/**
 * authUrlScrub v0.1 (T883, 2026-10-19): take sign-in tokens out of the address bar.
 *
 * A Google sign-in returns to /auth#access_token=...&refresh_token=...&provider_token=....
 * supabase-js (detectSessionInUrl, on by default) reads that fragment and, when the session
 * checks out, clears it with `location.hash = ''`, which leaves a bare "#". When the check
 * fails (stale link, clock skew, network) it leaves the tokens where they are. This runs once
 * supabase-js has finished reading the address and, with history.replaceState (no reload, no
 * new history entry), removes every token key from the fragment and the query plus the bare
 * "#". It never touches ?code / ?token_hash (supabase-js removes ?code itself after a PKCE
 * exchange; the mailbox OAuth callbacks read their own ?code) and never signs anyone out.
 */
const TOKEN_KEYS = [
  "access_token",
  "refresh_token",
  "provider_token",
  "provider_refresh_token",
  "id_token",
  "expires_at",
  "expires_in",
  "token_type",
];
const TOKEN_RE = /(^|[?#&])(access_token|refresh_token|provider_token|provider_refresh_token|id_token)=/;

/** The address with token keys removed, or null when nothing needs to change. Exported for tests. */
export function scrubbedAuthUrl(href: string): string | null {
  const u = new URL(href);
  let changed = false;
  const hash = u.hash.replace(/^#/, "");
  if (TOKEN_RE.test(hash)) {
    const p = new URLSearchParams(hash);
    for (const k of TOKEN_KEYS) p.delete(k);
    // "type" (recovery / signup / magiclink) only means something next to the tokens.
    p.delete("type");
    const rest = p.toString();
    u.hash = rest ? "#" + rest : "";
    changed = true;
  }
  if (TOKEN_RE.test(u.search)) {
    for (const k of TOKEN_KEYS) u.searchParams.delete(k);
    changed = true;
  }
  const out = u.pathname + u.search + u.hash;
  // A bare trailing "#" (what `location.hash = ''` leaves behind).
  if (!changed && !href.endsWith("#")) return null;
  return out;
}

export function scrubAuthTokensFromUrl(): void {
  try {
    const next = scrubbedAuthUrl(window.location.href);
    if (next !== null) window.history.replaceState(window.history.state, "", next);
  } catch {
    // No URL / history API: nothing to scrub with.
  }
}
